import { QTagProps } from '../../../../QAtoms/Tag/Tag';
import z from 'zod';
import { V2QriResolverResponseSchema } from '../../../Table/DataProviders/QuickSearchV2/types';

export const Task = z.object({
  id: z.number(),
  code: z.string(),
  status: z.string(),
  title: z.string(),
  description: z.string(),
  owner_id: z.number(),
  creator_id: z.number(),
  created_at: z.number(),
  updated_at: z.number(),
  closed_at: z.number().nullish(),
  time_limit: z.number(),
  type: z.string(),
  parent_qri: z.string(),
});
export type Task = z.infer<typeof Task>;

export type ResolvedTask = Task & {
  source: {
    label: string;
    link: string | null;
    domain: string;
  };
  assignee: string;
};

export const TaskStatus = {
  OPEN: 'open',
  CLOSED_SUCCESS: 'closed_success',
  CLOSED_FAIL: 'closed_fail',
} as const;

export type TaskStatusType = (typeof TaskStatus)[keyof typeof TaskStatus];

export type StatusConfig = {
  label: string;
  color: QTagProps['variantColor'];
};

export const TaskStatusToStatusConfigMap = {
  [TaskStatus.OPEN]: {
    label: 'Open',
    color: 'blue',
  },
  [TaskStatus.CLOSED_SUCCESS]: {
    label: 'Completed',
    color: 'green',
  },
  [TaskStatus.CLOSED_FAIL]: {
    label: 'Canceled',
    color: 'red',
  },
} as const satisfies Record<TaskStatusType, StatusConfig>;

export const MedtechUser = z.object({
  user_id: z.number(),
  full_name: z.string(),
  active: z.boolean().nullish(),
});
export type MedtechUser = z.infer<typeof MedtechUser>;

export const QriDetailsResponse = z.object({
  data: z.object({
    items: V2QriResolverResponseSchema,
    total: z.number(),
  }),
});
export type QriDetailsResponse = z.infer<typeof QriDetailsResponse>;

// TasksDrawer Event System Types
export type QTasksDrawerProps = {
  taskId?: string;
  viewOnly?: boolean;
};

/**
 * Return type for the useTasksDrawerEventListener hook.
 * Includes all drawer props plus isOpen state and onClose handler.
 */
export type UseTasksDrawerEventListenerResult = QTasksDrawerProps & {
  /**
   * Whether the drawer should be open.
   * Derived from whether taskId is truthy.
   */
  isOpen: boolean;

  /**
   * Callback to close the drawer and dispatch the close event.
   */
  onClose: () => void;
};

/**
 * CustomEvent type for the drawer update event.
 */
type TasksDrawerUpdateEvent = CustomEvent<QTasksDrawerProps>;

// Extend global WindowEventMap for TypeScript type safety
declare global {
  interface WindowEventMap {
    'QTasksDrawer-update': TasksDrawerUpdateEvent;
    'QTasksDrawer-closed': CustomEvent;
  }
}
